import {
  confirmSignIn,
  fetchAuthSession,
  signIn,
  signOut,
  updatePassword,
  type SignInOutput,
} from "aws-amplify/auth";
import { AxiosError } from "axios";
import { logout } from "../api/auth";
import { loadConfig } from "../configuration/config";
import SSOProvider from "../types/auth/SSOProvider";
import { TokenPayload } from "../context/AuthContext";
import { ApiError } from "../api/helpers/errors";

export type LoginRequest = {
  email: string;
  password: string;
};

export type SetPasswordRequest = {
  email: string;
  currentPassword: string;
  password: string;
  requiredAttributeData: any;
};

export type ResetPasswordRequest = {
  oldPassword: string;
  newPassword: string;
};

export class AuthError extends Error {
  statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
    this.name = "AuthError";
  }
}

export default class AuthService {
  private static _instance: AuthService;

  public async getIdToken(): Promise<string> {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      throw new AuthError("Failed to get current session.", 401);
    }
    return idToken;
  }

  public async login(
    loginRequest: LoginRequest,
    handleLoginSuccess: (payload: TokenPayload) => void,
    handleNewPasswordRequired: (
      userAttributes: unknown,
      requiredAttributeData: unknown,
      email: string,
      currentPassword: string
    ) => void
  ): Promise<boolean> {
    const result = await signIn({
      username: loginRequest.email,
      password: loginRequest.password,
    });
    return await this.handleSignInResult(
      result,
      loginRequest.email,
      loginRequest.password,
      handleLoginSuccess,
      handleNewPasswordRequired
    );
  }

  public async handleNewPasswordChallenge(
    request: SetPasswordRequest,
    handleLoginSuccess: (payload: TokenPayload) => void
  ): Promise<void> {
    const result = await confirmSignIn({
      challengeResponse: request.password,
    });
    if (result.nextStep.signInStep !== "DONE") {
      throw new Error(
        `Unexpected sign-in step after password challenge: ${result.nextStep.signInStep}.`
      );
    }
    await this.completeLoginSuccess(handleLoginSuccess);
  }

  private async handleSignInResult(
    result: SignInOutput,
    email: string,
    currentPassword: string,
    handleLoginSuccess: (payload: TokenPayload) => void,
    handleNewPasswordRequired: (
      userAttributes: unknown,
      requiredAttributeData: unknown,
      email: string,
      currentPassword: string
    ) => void
  ): Promise<boolean> {
    const { signInStep } = result.nextStep;
    if (signInStep === "DONE") {
      await this.completeLoginSuccess(handleLoginSuccess);
      return true;
    }
    if (signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      handleNewPasswordRequired(
        null,
        result.nextStep.missingAttributes,
        email,
        currentPassword
      );
      return false;
    }
    throw new Error(`Unexpected sign-in step: ${signInStep}.`);
  }

  private async completeLoginSuccess(
    handleLoginSuccess: (payload: TokenPayload) => void
  ): Promise<void> {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload as TokenPayload | undefined;
    if (!payload) {
      throw new AuthError("Failed to get current session.", 401);
    }
    handleLoginSuccess(payload);
  }

  public async logout(onSuccess: () => void): Promise<void> {
    let accessToken: string | undefined;
    try {
      const session = await fetchAuthSession();
      accessToken = session.tokens?.accessToken?.toString();
    } catch {
      accessToken = undefined;
    }
    if (!accessToken) {
      await signOut();
      onSuccess();
      return;
    }
    try {
      await logout(accessToken);
      await signOut();
      onSuccess();
    } catch (error: unknown) {
      if (
        error instanceof ApiError &&
        error.cause instanceof AxiosError &&
        error.cause?.response?.status === 401
      ) {
        await signOut();
        onSuccess();
        return;
      }
      if (error instanceof Error) {
        console.error(error);
        throw error;
      }
      throw new Error(String(error));
    }
  }

  public async resetPassword(request: ResetPasswordRequest): Promise<void> {
    await updatePassword({
      oldPassword: request.oldPassword,
      newPassword: request.newPassword,
    });
  }

  public async getSSOUrl(provider: SSOProvider): Promise<string> {
    const config = await loadConfig();
    return `${config.CognitoDomain}/oauth2/authorize?${new URLSearchParams({
      identity_provider: provider,
      redirect_uri: config.CognitoCallback,
      response_type: "code",
      client_id: config.CognitoAppClientId,
      scope: "openid",
    })}`;
  }

  /* istanbul ignore next */
  public static getInstance(): AuthService {
    if (!AuthService._instance) {
      AuthService._instance = new AuthService();
    }
    return AuthService._instance;
  }
}
